import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Copy, Check, Heart, CreditCard, Banknote, ArrowRight, Wallet } from 'lucide-react';
import { createDonation, startMercadoPago, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const PRESET_AMOUNTS = [10000, 50000, 100000, 250000, 500000];

const BANK_INFO = {
  alias: 'fundacionfd.mp',
  cvu: '0000003100056538050572',
  account_name: 'FUNDACION FUTUROS DEL DEPORTE',
};

const Donate = () => {
  const [step, setStep] = useState(1); // 1: amount, 2: payment info, 3: confirm
  const [selectedAmount, setSelectedAmount] = useState(50000);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    company_name: '',
    notes: '',
  });

  const finalAmount = isCustom ? parseFloat(customAmount) || 0 : selectedAmount;

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copiado al portapapeles');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleAmountSubmit = () => {
    if (finalAmount < 1) {
      toast.error('Ingresá un monto válido');
      return;
    }
    setStep(2);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await createDonation({
        donor_name: formData.donor_name,
        donor_email: formData.donor_email,
        donor_phone: formData.donor_phone,
        company_name: formData.company_name || undefined,
        amount: finalAmount,
        payment_method: 'transfer',
        notes: formData.notes || undefined,
      });
      toast.success('¡Donación registrada! Verificaremos la transferencia.');
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al registrar');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMercadoPago = async () => {
    // Validate required fields like the form does
    if (!formData.donor_name || !formData.donor_email || !formData.donor_phone) {
      toast.error('Completá nombre, email y teléfono para pagar con Mercado Pago');
      return;
    }
    setIsLoading(true);
    try {
      const res = await startMercadoPago({
        donor_name: formData.donor_name,
        donor_email: formData.donor_email,
        donor_phone: formData.donor_phone,
        company_name: formData.company_name || undefined,
        amount: finalAmount,
        notes: formData.notes || undefined,
      });
      const initPoint = res.data?.init_point;
      if (!initPoint) {
        toast.error('No se pudo iniciar el pago');
        return;
      }
      toast.success('Redirigiendo a Mercado Pago...');
      // Redirect to Mercado Pago Checkout Pro
      window.location.href = initPoint;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al iniciar el pago');
      setIsLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedAmount(50000);
    setCustomAmount('');
    setIsCustom(false);
    setFormData({
      donor_name: '',
      donor_email: '',
      donor_phone: '',
      company_name: '',
      notes: '',
    });
  };

  return (
    <section id="donar" className="py-24 bg-gradient-to-b from-sky-600 to-blue-700 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-sky-300 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
            <Heart className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Tu donación cambia vidas</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Doná Ahora</h2>
          <p className="text-xl text-sky-100 max-w-2xl mx-auto">
            Cada aporte nos acerca un poco más al sueño de muchos jóvenes deportistas
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="p-6 sm:p-10 shadow-2xl border-0">
            {step === 1 && (
              <div data-testid="donate-step-amount">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Elegí un monto</h3>
                <p className="text-gray-600 mb-6">Todo aporte suma. Podés elegir uno sugerido o ingresar tu propio monto.</p>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                  {PRESET_AMOUNTS.map((amount) => (
                    <button
                      key={amount}
                      data-testid={`amount-preset-${amount}`}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setIsCustom(false);
                      }}
                      className={`px-4 py-4 rounded-xl border-2 transition-all font-bold ${
                        !isCustom && selectedAmount === amount
                          ? 'border-sky-500 bg-sky-50 text-sky-700 shadow-lg scale-105'
                          : 'border-gray-200 hover:border-sky-300 text-gray-700'
                      }`}
                    >
                      ${amount.toLocaleString('es-AR')}
                    </button>
                  ))}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">O ingresá un monto libre</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
                    <Input
                      data-testid="amount-custom-input"
                      type="number"
                      min="1"
                      step="any"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setIsCustom(true);
                      }}
                      onFocus={() => setIsCustom(true)}
                      placeholder="Ej: 75000"
                      className={`pl-9 text-lg py-6 border-2 ${
                        isCustom ? 'border-sky-500' : 'border-gray-200'
                      } focus:border-sky-500`}
                    />
                  </div>
                </div>

                <div className="bg-sky-50 border-2 border-sky-100 rounded-xl p-4 mb-6 text-center">
                  <p className="text-sm text-gray-600 mb-1">Vas a donar</p>
                  <p data-testid="amount-final" className="text-4xl font-bold text-sky-700">
                    ${finalAmount.toLocaleString('es-AR')}
                  </p>
                </div>

                <Button
                  data-testid="amount-continue-btn"
                  onClick={handleAmountSubmit}
                  size="lg"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white text-lg py-6 transition-colors group"
                >
                  Continuar
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="donate-step-payment">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Elegí cómo pagar</h3>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sky-600 hover:text-sky-700 text-sm font-medium"
                  >
                    ← Volver
                  </button>
                </div>

                {/* Mercado Pago - Online checkout */}
                <div className="bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50 border-2 border-sky-300 rounded-2xl p-5 mb-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Wallet className="w-5 h-5 text-sky-600" />
                        <span className="font-bold text-sky-900">Pagar online con Mercado Pago</span>
                        <span className="text-xs bg-sky-600 text-white px-2 py-0.5 rounded-full font-semibold">Recomendado</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        Tarjeta de crédito, débito, dinero en cuenta o transferencia. Acreditación automática.
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    👉 Completá primero tus datos al final del formulario, después clickeá:
                  </p>
                  <Button
                    data-testid="pay-mercadopago-btn"
                    type="button"
                    onClick={handleMercadoPago}
                    disabled={isLoading}
                    size="lg"
                    className="w-full bg-[#009ee3] hover:bg-[#0084c2] text-white text-lg py-6 transition-colors"
                  >
                    <Wallet className="w-5 h-5 mr-2" />
                    {isLoading ? 'Iniciando pago...' : `Pagar $${finalAmount.toLocaleString('es-AR')} con Mercado Pago`}
                  </Button>
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500 font-medium">o transferí manualmente</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-6 mb-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Banknote className="w-5 h-5 text-gray-600" />
                    <span className="font-semibold text-gray-900">Datos para transferencia bancaria</span>
                  </div>

                  <div className="space-y-3">
                    {[
                      { label: 'Alias', value: BANK_INFO.alias, key: 'alias' },
                      { label: 'CVU', value: BANK_INFO.cvu, key: 'cvu' },
                      { label: 'Titular', value: BANK_INFO.account_name, key: 'name' },
                    ].map((item) => (
                      <div key={item.key} className="bg-white rounded-lg p-3 flex items-center justify-between border border-sky-100">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-0.5">{item.label}</p>
                          <p className="text-gray-900 font-mono text-sm break-all">{item.value}</p>
                        </div>
                        <button
                          data-testid={`copy-${item.key}-btn`}
                          onClick={() => copyToClipboard(item.value, item.key)}
                          className="ml-3 p-2 hover:bg-sky-100 rounded-lg transition-colors flex-shrink-0"
                          aria-label={`Copiar ${item.label}`}
                        >
                          {copiedField === item.key ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <Copy className="w-5 h-5 text-sky-600" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-900">
                      <strong>Monto:</strong> ${finalAmount.toLocaleString('es-AR')}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 mb-6">
                  <p className="font-semibold text-gray-900 mb-2 flex items-center">
                    <CreditCard className="w-4 h-4 mr-2 text-sky-600" />
                    Pasos a seguir:
                  </p>
                  <ol className="text-sm text-gray-700 space-y-1.5 list-decimal list-inside">
                    <li>Copiá el Alias o CVU de arriba</li>
                    <li>Realizá la transferencia desde tu app de homebanking o Mercado Pago</li>
                    <li>Completá el formulario debajo para que podamos verificar tu donación</li>
                  </ol>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <h4 className="text-lg font-bold text-gray-900">Confirmá tu donación</h4>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre completo *</label>
                      <Input
                        data-testid="donor-name-input"
                        name="donor_name"
                        value={formData.donor_name}
                        onChange={handleChange}
                        required
                        placeholder="Tu nombre"
                        className="border-2 border-gray-200 focus:border-sky-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Empresa (opcional)</label>
                      <Input
                        data-testid="donor-company-input"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Razón social"
                        className="border-2 border-gray-200 focus:border-sky-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <Input
                        data-testid="donor-email-input"
                        type="email"
                        name="donor_email"
                        value={formData.donor_email}
                        onChange={handleChange}
                        required
                        placeholder="vos@empresa.com"
                        className="border-2 border-gray-200 focus:border-sky-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono *</label>
                      <Input
                        data-testid="donor-phone-input"
                        type="tel"
                        name="donor_phone"
                        value={formData.donor_phone}
                        onChange={handleChange}
                        required
                        placeholder="+54 ..."
                        className="border-2 border-gray-200 focus:border-sky-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Notas (opcional)</label>
                    <Textarea
                      data-testid="donor-notes-input"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Comprobante, fecha de transferencia, mensaje..."
                      className="border-2 border-gray-200 focus:border-sky-400 resize-none"
                    />
                  </div>

                  <Button
                    data-testid="donate-confirm-btn"
                    type="submit"
                    disabled={isLoading}
                    size="lg"
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white text-lg py-6 transition-colors"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    {isLoading ? 'Registrando...' : `Confirmé mi donación de $${finalAmount.toLocaleString('es-AR')}`}
                  </Button>
                </form>
              </div>
            )}

            {step === 3 && (
              <div data-testid="donate-step-success" className="text-center py-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-12 h-12 text-green-600 fill-green-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">¡Gracias por tu donación!</h3>
                <p className="text-lg text-gray-600 mb-2">
                  Registramos tu aporte de <span className="font-bold text-sky-600">${finalAmount.toLocaleString('es-AR')}</span>
                </p>
                <p className="text-gray-500 mb-8">
                  Vamos a verificar la transferencia y te enviaremos un comprobante al email proporcionado.
                </p>
                <Button
                  onClick={reset}
                  variant="outline"
                  className="border-2 border-sky-300 text-sky-700 hover:bg-sky-50"
                >
                  Hacer otra donación
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Donate;
